import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'primereact/calendar'
import FormField from '../FormField'
import { resolveFieldError } from '../../../lib/forms/messages'
import { parseISODate, toISODate } from '../../../lib/datetime'

/**
 * RHF-bağlı tarih alanı (029). `Controller` + PrimeReact `Calendar` + `FormField` — tarih girişleri için tek
 * kaynak (Reuse Mandate); Calendar dili aktif PrimeReact locale'inden gelir (bkz. lib/primeLocale) → seçili
 * uygulama diline uyar. Form değeri "YYYY-MM-DD" string tutulur (şema/DTO ile uyumlu); Calendar'a Date olarak
 * verilir, geri string'e çevrilir.
 */
interface FormDateFieldProps<T extends FieldValues> {
  control: Control<T>
  name: FieldPath<T>
  label: string
  required?: boolean
  full?: boolean
  /** Seçilebilecek en erken gün; yalnız yeni seçimi kısıtlar (kurumsal gün: `todayStart`). */
  minDate?: Date
  /** Etiketi görsel olarak gizler (bkz. `FormField.hideLabel`). */
  hideLabel?: boolean
  /** Kullanıcı değeri değiştirdiğinde çağrılır (form `reset`'inde çağrılmaz). */
  onValueChange?: (value: string) => void
  /** Değer gösterilir ama değiştirilemez (ör. Ajanda "Serinin tamamı"nda gün kilidi). */
  disabled?: boolean
}

export default function FormDateField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  full,
  minDate,
  hideLabel,
  onValueChange,
  disabled,
}: Readonly<FormDateFieldProps<T>>) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormField
          label={label}
          htmlFor={name}
          required={required}
          full={full}
          hideLabel={hideLabel}
          error={resolveFieldError(fieldState.error?.message, t)}
        >
          <Calendar
            inputId={name}
            value={parseISODate(field.value)}
            onChange={(e) => {
              const next = toISODate(e.value ?? null)
              const changed = next !== field.value
              field.onChange(next)
              if (changed) onValueChange?.(next)
            }}
            onBlur={field.onBlur}
            dateFormat="dd.mm.yy"
            showIcon
            minDate={minDate}
            disabled={disabled}
            className={fieldState.error ? 'p-invalid' : undefined}
          />
        </FormField>
      )}
    />
  )
}
